import { createContext, useCallback, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from 'src/utils/axios';
import {
  cleanupTokens,
  isTokenMetaValid,
  removeUserPilotFromLocalStorage,
  setSessionTokenDetails,
} from 'src/utils/jwt';
import { performComprehensiveLogout } from 'src/utils/logout';
import { useDispatch, useStore } from 'react-redux';
import { DASHBOARD_GROUPS } from '../utils/dashboard';
import { datadogRum } from '@datadog/browser-rum';
import { environmentsWithDatadog, getEnvironmentName } from 'src/AppUtils';
import { userService } from '../services/User.service';
import { setHouseholdMember, setMemberType } from '../redux/slices/Main/main';
import { setCertification, setHousehold } from '../redux/slices/Tenant/Tenant';
import useBroadcastChannel from 'src/hooks/useBroadcastChannel';
import { getFeatureFlags } from 'src/redux/slices/FeatureFlags';
import {
  setUser as setNewRenterInterfaceUser,
  clearUser as clearNewRenterInterfaceUser,
  setSavedSignature,
  setSavedInitials,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import {
  clearCertifications,
  patchCertificationsSignatures,
  setCertifications,
  setCertStatus,
  setCertStatusLoading,
  setUnconfirmedHouseholds,
  setUserHouseholds,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import { isLocalhost } from 'src/utils/isLocalHost';
import { setupAllNotificationPreferences } from 'src/utils/notificationPreferences';
import { Userpilot } from 'userpilot';

// ----------------------------------------------------------------------

const identifyUserPilot = (user, certs = []) => {
  const token = import.meta.env.VITE_USERPILOT_KEY || 'STG-NX-44208137';

  const isRenter = user.groups
    .map((g) => g.name.toLowerCase())
    .includes('resident');

  const certProperties = certs
    .map((c) => c?.property)
    .filter(Boolean)
    .filter((p, i, arr) => arr.findIndex((x) => x.id === p.id) === i);

  // Member type from certs (ULHHM v2: certification-scoped)
  let memberType = null;
  for (const cert of certs) {
    // Already extracted by fetchRenterCertificationsV2
    if (cert.member_type?.display_name) {
      memberType = cert.member_type.display_name;
      break;
    }
    // Raw from fetchMyCerts: dig into cert_household.cert_members
    const certMembers = cert?.cert_household?.cert_members ?? [];
    const myMember = certMembers.find((m) => m.user_id === user.id);
    if (myMember?.member_type_name) {
      memberType = myMember.member_type_name;
      break;
    }
  }

  const newRenterInterfaceEnabled = certProperties.some(
    (p) => p.enable_new_renters_experience === true
  );

  const traits = {
    name: user.displayName,
    email: user.email,
    created_at: user.date_joined,
    is_renter: isRenter,
    is_compliance: user.isCompliance,
    has_accepted_tos: !!user.terms_of_service_agreement,
  };

  if (certProperties.length > 0) {
    traits.property_names = certProperties.map((p) => p.name).join(', ');
    traits.new_renter_interface_enabled = newRenterInterfaceEnabled;
  }
  if (memberType) {
    traits.member_type = memberType;
  }

  Userpilot.identify(user.id.toString(), traits);
};

const identifyDataDogUser = (user) => {
  if (!environmentsWithDatadog.includes(getEnvironmentName())) {
    return;
  }

  datadogRum.setUser({
    id: user.id,
    name: user.displayName,
    email: user.email,
    company: user?.property_owner_companies[0]?.name || '',
    property: user?.property?.name || '',
  });
};

// calculates the user permissions and identify the user on the different
// external services
const buildUserAuthPayload = async (userdata = {}) => {
  const user = { ...userdata };

  user.displayName = `${user.first_name} ${user.last_name}`;
  user.hasDashboard = !!user.groups
    .map((g) => g.name.toLowerCase())
    .find(
      (g) =>
        g === DASHBOARD_GROUPS.compliance_specialist.toLowerCase() ||
        g === DASHBOARD_GROUPS.compliance_manager.toLowerCase()
    );
  user.isProntoAdmin = !!user.groups
    .map((g) => g.name.toLowerCase())
    .find((g) => g === 'pronto admin');
  user.isUserAdministrator =
    !!user.is_superuser ||
    !!user.groups
      .map((g) => g.name.toLowerCase())
      .find((g) => g === 'user administrator' || g === 'django user - eng');
  user.isCompliance = !!user.groups
    .map((g) => g.name.toLowerCase())
    .find((g) => g === 'compliance');
  user.isComplianceManager = !!user.groups
    .map((g) => g.name.toLowerCase())
    .find((g) => g === DASHBOARD_GROUPS.compliance_manager.toLowerCase());
  user.isComplianceSpecialist = !!user.groups
    .map((g) => g.name.toLowerCase())
    .find((g) => g === DASHBOARD_GROUPS.compliance_specialist.toLowerCase());
  user.isCertificationWorkbench =
    !!user.groups
      .map((g) => g.name.toLowerCase())
      .find((g) => g === 'certification workbench') && user.isComplianceManager;
  user.isBulkCommunications = !!user.groups
    .map((g) => g.name.toLowerCase())
    .find((g) => g === DASHBOARD_GROUPS.bulk_communications.toLowerCase());
  user.canUnapprovedCertification = !!user.groups
    .map((g) => g.name.toLowerCase())
    .find((g) => g === 'can unapproved certification');
  user.canRemoveHouseholdMembers = !!user.groups
    .map((g) => g.name.toLowerCase())
    .find((g) => g === 'compliance can delete hhm');
  user.canDeleteQuestionnaire = !!user.groups
    .map((g) => g.name.toLowerCase())
    .find((g) => g === 'can delete questionnaire');

  user.canSyncPMS = !!user.groups
    .map((group) => group.name.toLowerCase())
    .find((group) => group === 'pms sync');

  // --- Notification Access Control & Preferences ---
  // See integration-docs.md for field names
  user.canUseRealTimeNotificationsFileOwner =
    !!userdata.can_use_real_time_notifications_file_owner;
  user.canUseRealTimeNotificationsWatched =
    !!userdata.can_use_real_time_notifications_watched;
  user.canUseRealTimeNotificationsProperty =
    !!userdata.can_use_real_time_notifications_property;
  user.canUseWebSockets = !!userdata.can_use_websockets;
  user.lotteryEligible = !!userdata.lottery_eligible;
  // ENG-4183 Campaigns — the server's flag + permission + property-scope gate,
  // and the per-action codenames the UI hides buttons with.
  user.campaignsEligible = !!userdata.campaigns_eligible;
  user.campaignPerms = Array.isArray(userdata.campaign_perms)
    ? userdata.campaign_perms
    : [];
  user.canUseMyWorkBoard = !!userdata.can_use_my_work_board;
  user.isWorkgroupManager = !!userdata.is_workgroup_manager;
  user.connectChatEnabled = !!userdata.connect_chat_enabled;
  // ENG-4137 — the in-app Support Case stream. One global flag: on for every
  // compliance user or no one, and turning it off returns the FAB to Connect chat.
  user.supportCasesEnabled = !!userdata.support_cases_enabled;
  // ENG-4211 — the hub's Schedules tab. The server already folds the case gate and
  // the schedules flag together; the client only mirrors it.
  user.supportCaseSchedulesEnabled = !!userdata.support_case_schedules_enabled;

  // Set up notification preferences using utility function
  setupAllNotificationPreferences(user, userdata);
  // --- End Notification Access Control ---

  const currentRoles = user.groups.map((role) => role.name.toLowerCase());
  if (!currentRoles.includes('resident')) {
    // teams/compliance_team fetch removed (ENG-2047) - Team Renters now uses property-based access
  }

  identifyDataDogUser(user);
  identifyUserPilot(user);

  return user;
};

// ULHHM (HHM_V2_READ always on): the legacy household_members chain is empty,
// so the renter's certifications are fetched from the certification list
// endpoint, scoped server-side by Certification.objects.for_user (v2
// CertificationMember chain).  No legacy household filter is sent.
// cert_household is expanded so TenantCertification's v2 questionnaire path
// engages, and the requesting user's member_type is derived from
// cert_household.household_members for display (TenantPropertyCard /
// setMemberType).
const fetchRenterCertificationsV2 = async (user) => {
  try {
    const { data } = await userService.fetchRenterCertifications();
    const certifications = Array.isArray(data) ? data : data?.results ?? [];

    const mapped = certifications.map((certification) => {
      const members = certification?.cert_household?.household_members ?? [];
      const myMember = members.find((m) => m.user_id === user.id);
      certification.member_type = myMember
        ? {
            id: myMember.member_type_id,
            name: (myMember.member_type_name || '').toLowerCase(),
            display_name: myMember.member_type_name,
            weight: myMember.member_type_weight,
          }
        : certification.member_type || { name: '' };

      const neededSignatures = [];
      (certification.form_signatures_request || [])
        .filter((form) => form.requested)
        .forEach((forms_requested) => {
          const filteredSignatures = forms_requested.needed_signatures.filter(
            (signature) =>
              !signature.is_date &&
              !signature.is_name &&
              !signature.is_hhmt &&
              !signature.is_phone &&
              !signature.compliance
          );
          neededSignatures.push(...filteredSignatures);
        });
      certification.neededSignatures = neededSignatures;
      return certification;
    });

    const activeCertifications = mapped.filter((certification) => {
      return certification.status?.renter_active === true;
    });
    activeCertifications.sort(sorCertificationByCreationDate);
    return activeCertifications || [];
  } catch {
    return null;
  }
};

const sorCertificationByCreationDate = (currentItem, nextItem) => {
  const currentItemCreationDate = new Date(currentItem.created);
  const nextItemCreationDate = new Date(nextItem.created);
  if (currentItemCreationDate < nextItemCreationDate) {
    return 1;
  }
  if (currentItemCreationDate > nextItemCreationDate) {
    return -1;
  }
  return 0;
};

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  GET_USER: (state, action) => {
    const { user } = action.payload;

    // Preserve activeCertifications and currentCertification if they exist in current state
    // This prevents losing certifications when getUser() is called after phone updates/deletions
    // certifications/certificationIds for new experience live in NewRenterInterface slice
    const preservedUser = {
      ...user,
      ...(state.user?.activeCertifications && {
        activeCertifications: state.user.activeCertifications,
      }),
      ...(state.user?.currentCertification && {
        currentCertification: state.user.currentCertification,
      }),
    };

    return {
      ...state,
      user: preservedUser,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  MODIFY_ACTIVE_CERTIFICATIONS: (state, action) => {
    const { activeCertifications } = action.payload;
    return {
      ...state,
      user: {
        ...state.user,
        activeCertifications: activeCertifications,
      },
    };
  },
  UPDATE_USER_PROFILE: (state, action) => {
    const { updatedData } = action.payload;
    return {
      ...state,
      user: {
        ...state.user,
        ...updatedData,
      },
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: (_access, _refresh) => Promise.resolve(),
  extendCurrentSession: (_) => Promise.resolve(),
  logout: () => Promise.resolve(),
  sendMessage: (_) => null,
});

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const rDispatch = useDispatch();
  const store = useStore();
  const { message, sendMessage } = useBroadcastChannel('auth-channel');

  useEffect(() => {
    if (!message) {
      return;
    }
    if (message.force_reload) {
      window.location.reload();
    }
    if (message.refresh) {
      if (!state.isAuthenticated) {
        window.location.reload();
      }
    }
  }, [message, state.isAuthenticated]);

  const addHubspotCloseButton = useCallback(() => {
    if (environmentsWithDatadog.includes(getEnvironmentName())) {
      return;
    }
    const observer = new MutationObserver(() => {
      const chatWidget = document.querySelector(
        '#hubspot-messages-iframe-container'
      );
      if (chatWidget && !document.querySelector('#hs-chat-close-btn')) {
        const closeButton = document.createElement('div');
        closeButton.id = 'hs-chat-close-btn';
        closeButton.innerText = '✖';
        closeButton.className = 'hubspot-close-chat-button';

        closeButton.addEventListener('click', () => {
          window.HubSpotConversations?.widget?.remove();
          closeButton.style.display = 'none';
        });

        chatWidget.appendChild(closeButton);
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }, []);

  const enableChatWidget = useCallback(() => {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.load();
      addHubspotCloseButton();
      return;
    }
    window.hsConversationsOnReady = [
      () => {
        window.HubSpotConversations?.widget.load();
        addHubspotCloseButton();
      },
    ];
  }, [addHubspotCloseButton]);

  const startHubspotAuthentication = useCallback(() => {
    window.hsConversationsSettings = {
      loadImmediately: false,
      enableWidgetCookieBanner: true,
    };
    userService
      .authenticateHubspotUser()
      .then((response) => {
        const { token } = response.data;
        window.hsConversationsSettings = {
          ...window.hsConversationsSettings,
          identificationEmail: state.user.email,
          identificationToken: token,
        };
        enableChatWidget();
      })
      .catch(() => {
        enableChatWidget();
      });
  }, [state?.user?.email, enableChatWidget]);

  useEffect(() => {
    if (isLocalhost) {
      return;
    }
    // Connect Chat and HubSpot are mutually exclusive support channels.
    // useConnectChat picks Connect when this flag is on; loading HubSpot's
    // script unconditionally here would show both widgets at once. Feature
    // flags are always loaded (awaited in the initialize() effect below)
    // before isAuthenticated flips true, so this read is never stale.
    const connectChatEnabled =
      store.getState()?.featureFlags?.flags?.CONNECT_CHAT_ENABLED;
    if (connectChatEnabled) {
      return;
    }
    if (state.isInitialized && state.isAuthenticated) {
      const script = document.createElement('script');
      script.src = '//js.hs-scripts.com/47354623.js';
      script.async = true;
      script.defer = true;
      script.type = 'text/javascript';
      script.id = 'hs-script-loader';

      if (!document.getElementById('hs-script-loader')) {
        document.head.appendChild(script);
        startHubspotAuthentication();
      }

      return () => {
        const existingScript = document.getElementById('hs-script-loader');
        if (existingScript) {
          document.head.removeChild(existingScript);
        }
      };
    }
  }, [
    state.isInitialized,
    state.isAuthenticated,
    startHubspotAuthentication,
    store,
  ]);

  useEffect(() => {
    const initialize = async () => {
      let payload = {
        isAuthenticated: false,
        user: null,
      };
      try {
        if (window.location.pathname === '/auth/impersonate-user') {
          return;
        }
        removeUserPilotFromLocalStorage();
        if (!isTokenMetaValid('refreshTokenMetadata')) {
          cleanupTokens();
          dispatch({
            type: 'INITIALIZE',
            payload,
          });
          return;
        }

        await rDispatch(getFeatureFlags());
        const { data: userResponse } = await axios.get(
          'user_profile/user/current_profile/'
        );
        const user = await buildUserAuthPayload(userResponse);
        await getUserCertifications(user);
        rDispatch(setNewRenterInterfaceUser({ ...user }));
        payload = {
          isAuthenticated: true,
          user,
        };
      } catch {
        // Handle error silently
      } finally {
        dispatch({
          type: 'INITIALIZE',
          payload,
        });
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rDispatch]);

  const getUser = async () => {
    let payload = {};
    try {
      const { data: userResponse } = await axios.get(
        'user_profile/user/current_profile/'
      );
      const user = await buildUserAuthPayload(userResponse);
      rDispatch(setNewRenterInterfaceUser({ ...user }));
      payload = {
        user,
      };
    } catch {
      // Handle error silently
    } finally {
      dispatch({
        type: 'GET_USER',
        payload,
      });
    }
  };

  const getUserCertifications = async (user) => {
    const currentRoles = user.groups.map((role) => role.name.toLowerCase());
    if (currentRoles.includes('resident')) {
      let key;
      let certificationsList = [];
      if (user.sees_new_experience) {
        try {
          key = 'certifications';
          const { data: certificationsData } = await userService.fetchMyCerts();
          (certificationsData || []).sort(sorCertificationByCreationDate);
          certificationsList = certificationsData || [];
          identifyUserPilot(user, certificationsList);
          rDispatch(setCertifications(certificationsList));
          rDispatch(setUnconfirmedHouseholds(user.id));

          rDispatch(setCertStatusLoading(true));
          userService
            .fetchCertStatus(certificationsList.map((c) => c.id))
            .then((data) => rDispatch(setCertStatus(data ?? {})))
            .catch((err) => {
              console.error('Failed to fetch cert status', err);
              rDispatch(setCertStatus({}));
            })
            .finally(() => rDispatch(setCertStatusLoading(false)));

          userService
            .fetchMyHousehold()
            .then(({ data }) => {
              rDispatch(setUserHouseholds(data ?? []));
            })
            .catch((err) => {
              console.error('Failed to fetch household members', err);
              rDispatch(setUserHouseholds([]));
            });

          userService
            .fetchFormSignatureRequests()
            .then((fsrs) => {
              rDispatch(patchCertificationsSignatures(fsrs));
            })
            .catch((sigErr) => {
              console.error('Failed to fetch form signatures', sigErr);
            });

          userService
            .fetchSavedSignature()
            .then((sig) => {
              rDispatch(setSavedSignature(sig?.signature_data ?? null));
            })
            .catch((sigErr) => {
              console.error('Failed to fetch saved signature', sigErr);
              rDispatch(setSavedSignature(null));
            });

          userService
            .fetchSavedInitials()
            .then((initials) => {
              if (initials?.signature_data) {
                rDispatch(setSavedInitials({ xfdf: initials.signature_data }));
              } else {
                rDispatch(
                  setSavedInitials({
                    text: initials?.initials_text ?? null,
                    font: initials?.font_family ?? null,
                  })
                );
              }
            })
            .catch((initialsErr) => {
              console.error('Failed to fetch saved initials', initialsErr);
              rDispatch(setSavedInitials({ text: null, font: null }));
            });
        } catch (error) {
          console.error('Failed to fetch certifications', error);
          rDispatch(setCertifications([]));
        }
      } else {
        user.activeCertifications = await fetchRenterCertificationsV2(user);
        identifyUserPilot(user, user.activeCertifications || []);
        key = 'activeCertifications';
      }

      const certsForSelection =
        key === 'certifications' ? certificationsList : user[key];

      // For legacy (non-new-experience) users, keep activeCertifications in sync
      // for the household selection flow. New-experience users use certificationsList
      // dispatched above via setCertifications — don't overwrite it here.
      if (key !== 'certifications') {
        user.activeCertifications = certsForSelection || [];
        dispatch({
          type: 'MODIFY_ACTIVE_CERTIFICATIONS',
          payload: { activeCertifications: user.activeCertifications },
        });
      }

      if (certsForSelection?.length === 1) {
        setNewTenantCertification(user, certsForSelection[0]);
        if (!user.sees_new_experience) {
          localStorage.setItem(
            'tenantCurrentCertificationID',
            JSON.stringify(certsForSelection[0].id)
          );
        }
        return;
      }
      const localStorageCertificationID = localStorage.getItem(
        'tenantCurrentCertificationID'
      );
      if (localStorageCertificationID && certsForSelection) {
        const currentCertificationID = JSON.parse(localStorageCertificationID);
        const certificationIndex = certsForSelection.findIndex(
          (certification) => certification.id === currentCertificationID
        );
        if (certificationIndex !== -1) {
          setNewTenantCertification(
            user,
            certsForSelection[certificationIndex]
          );
        } else {
          localStorage.removeItem('tenantCurrentCertificationID');
        }
      }
    }
  };

  const setNewTenantCertification = (user, certification) => {
    user.currentCertification = certification;
    rDispatch(setCertification(certification));
    rDispatch(setHousehold(certification?.household || null));
    rDispatch(setMemberType(certification?.member_type?.name || ''));
    rDispatch(setHouseholdMember(certification?.household) || null);
  };

  const login = async (access, refresh) => {
    setSessionTokenDetails(access, refresh);

    const { payload: flags } = await rDispatch(getFeatureFlags());
    const { data: userResponse } = await axios.get(
      'user_profile/user/current_profile/'
    );
    sendMessage({ refresh });
    const user = await buildUserAuthPayload(userResponse, flags);
    await getUserCertifications(user);
    rDispatch(setNewRenterInterfaceUser({ ...user }));

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });

    return user;
  };

  const extendCurrentSession = useCallback(({ access, refresh }) => {
    setSessionTokenDetails(access, refresh);
  }, []);

  // Stable identity: SessionExpiryAlert's logout timer keys off this callback,
  // and a new identity on every provider render re-armed the expiry effect
  // (re-opening the dialog after "Ignore for now") — ENG-3395.
  const logout = useCallback(async () => {
    try {
      // Call backend logout endpoint (includes cookies via axios)
      await userService.logout(state.user.impersonator?.session_id);
      // Perform frontend cleanup only (backend already handled above)
      performComprehensiveLogout();
      rDispatch(clearCertifications());
      rDispatch(clearNewRenterInterfaceUser());
      dispatch({ type: 'LOGOUT' });
      sendMessage({ force_reload: true });
    } catch {
      // Fallback to comprehensive logout if backend call fails
      performComprehensiveLogout();
      rDispatch(clearCertifications());
      rDispatch(clearNewRenterInterfaceUser());
      dispatch({ type: 'LOGOUT' });
      sendMessage({ force_reload: true });
    }
  }, [dispatch, rDispatch, sendMessage, state.user]);

  const resetPassword = async (email) => {
    return await fetch(
      (import.meta.env.VITE_API_ROOT || 'http://localhost:8000/api/') +
        'user_profile/request-password-reset-email/',
      {
        method: 'POST',
        body: JSON.stringify({ email: email }),
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
      }
    );
  };

  const resetPasswordByUser = async (userId) => {
    return await axios.post('user_profile/request-password-reset-by-user/', {
      user_id: userId,
    });
  };

  const updatePassword = async (data) => {
    await axios.patch('user_profile/password-reset-complete/', data);
  };

  const modifyActiveCertifications = async (activeCertifications) => {
    dispatch({
      type: 'MODIFY_ACTIVE_CERTIFICATIONS',
      payload: { activeCertifications: activeCertifications },
    });
  };

  const updateUserProfile = async (updatedData) => {
    dispatch({
      type: 'UPDATE_USER_PROFILE',
      payload: { updatedData },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        extendCurrentSession,
        logout,
        resetPassword,
        resetPasswordByUser,
        updatePassword,
        setNewTenantCertification,
        sendMessage,
        modifyActiveCertifications,
        updateUserProfile,
        getUserCertifications,
        getUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
